import { IProductBaseSearchResultsData } from 'models/search/productbasesearchresults';
import { IRefinementGroup, IRefinement } from 'models/filtering/refinement';
import * as c from 'models/constants';
import {
	setRefinementGroups as setProductRefinementGroups,
	setRefinementSelections as setProductRefinementSelections,
	setLastRefinementItemSelection as setProductLastRefinementItemSelection,
	setNarrowRefinement as setNarrowProductRefinement,
	getProductSearchResults,
} from '../search/productsearchresults';
import {
	setRefinementGroups as setWardrobeRefinementGroups,
	setRefinementSelections as setWardrobeRefinementSelections,
	setLastRefinementItemSelection as setWardrobeLastRefinementItemSelection,
	setNarrowRefinement as setNarrowWardrobeRefinement,
	getWardrobeItemsSearchResults,
} from '../search/wardrobeitemsearchresults';
import { IStore } from 'redux/IStore';
import { SearchUsage } from 'models/enums/searchusage';

export class ProductFiltering {
	private dispatch: any;
	private nodeType: string;
	private usage: SearchUsage;
	private productSearchResults: IProductBaseSearchResultsData;
	private setRefinementGroups: (refinementGroups: any) => void;
	private setRefinementSelections: (refinementGroups: IRefinementGroup[]) => void;
	private setLastRefinementItemSelection: (
		lastSelectedRefinementItemId: number,
		lastSelectedRefinementGroupId: number,
	) => void;
	private setNarrowRefinement: (lastActionWasToNarrowRefinement: boolean) => void;
	private getSearchResults: (isFirstLoad: boolean, isRefinement: boolean, usage: SearchUsage) => void;

	constructor(dispatch: any, state: IStore, nodeType: string, usage?: SearchUsage) {
		this.dispatch = dispatch;
		this.nodeType = nodeType;
		this.usage = usage;

		if (this.nodeType === c.NODE_TYPE_PRODUCT) {
			this.setRefinementGroups = setProductRefinementGroups;
			this.setRefinementSelections = setProductRefinementSelections;
			this.setLastRefinementItemSelection = setProductLastRefinementItemSelection;
			this.setNarrowRefinement = setNarrowProductRefinement;
			this.productSearchResults = state.productSearchResults;
			this.getSearchResults = getProductSearchResults;
		} else if (this.nodeType === c.NODE_TYPE_WARDROBE) {
			this.setRefinementGroups = setWardrobeRefinementGroups;
			this.setRefinementSelections = setWardrobeRefinementSelections;
			this.setLastRefinementItemSelection = setWardrobeLastRefinementItemSelection;
			this.setNarrowRefinement = setNarrowWardrobeRefinement;
			this.productSearchResults = state.wardrobeItemSearchResults;
			this.getSearchResults = getWardrobeItemsSearchResults;
		}
	}

	//METHODS:

	public refineSearch(refinementGroupId: number, refinementItemId: number) {
		if (this.productSearchResults.isFetching === false) {
			this.dispatch(this.setLastRefinementItemSelection(refinementItemId, refinementGroupId));

			if (!this.isDisabled(refinementGroupId, refinementItemId)) {
				if (this.isAvailable(refinementGroupId, refinementItemId)) {
					this.dispatch(this.setNarrowRefinement(true));
					this.saveRefinementToFilterSelection(refinementGroupId, refinementItemId);
					this.dispatch(this.getSearchResults(true, true, this.usage));
				} else if (this.isSelectedButDisabled(refinementGroupId, refinementItemId)) {
					this.removeRefinementFromFilterSelection(refinementGroupId, refinementItemId, true);
				} else {
					this.dispatch(this.setNarrowRefinement(false));
					this.removeRefinementFromFilterSelection(refinementGroupId, refinementItemId, null, true);
					this.dispatch(this.getSearchResults(true, true, this.usage));
				}
			}
		}
	}

	// Methods to store, retrieve and update the refinement selections
	// ===============================================================
	// remove refinement from selection
	private removeRefinementFromFilterSelection(
		refinementGroupId: number,
		refinementItemId: number,
		disable?: boolean,
		available?: boolean,
	) {
		const refinementSelectionGroup = this.productSearchResults.refinementSelections.filter(
			(x) => x.groupId === refinementGroupId,
		)[0];
		refinementSelectionGroup.refinements.splice(
			this.findIndexOfRefinement(refinementSelectionGroup.refinements, refinementItemId),
			1,
		);

		if (disable) {
			this.setAsDisabled(refinementGroupId, refinementItemId);
		}
		if (available) {
			this.setAsAvailable(refinementGroupId, refinementItemId);
		}

		this.dispatch(this.setRefinementSelections(this.productSearchResults.refinementSelections));
		this.dispatch(this.setRefinementGroups(this.productSearchResults.refinementGroups));
	}

	// add refinement to selection
	private saveRefinementToFilterSelection(refinementGroupId: number, refinementItemId: number) {
		let refinementSelectionGroup: IRefinementGroup = {};

		// no selections
		if (this.productSearchResults.refinementSelections.length === 0) {
			refinementSelectionGroup.groupId = refinementGroupId;
			refinementSelectionGroup.refinements = [];
			refinementSelectionGroup.refinements.push({ id: refinementItemId });
			this.productSearchResults.refinementSelections.push(refinementSelectionGroup);
		} else {
			refinementSelectionGroup = this.productSearchResults.refinementSelections.filter(
				(x) => x.groupId === refinementGroupId,
			)[0];
			// no group selections
			if (!refinementSelectionGroup) {
				refinementSelectionGroup = {};
				refinementSelectionGroup.groupId = refinementGroupId;
				refinementSelectionGroup.refinements = [];
				refinementSelectionGroup.refinements.push({ id: refinementItemId });
				this.productSearchResults.refinementSelections.push(refinementSelectionGroup);
			} else {
				const refinement = refinementSelectionGroup.refinements.filter((x) => x.id === refinementItemId)[0];
				if (!refinement) {
					refinementSelectionGroup.refinements.push({ id: refinementItemId });
				}
			}
		}

		// set item in refinementGroups to selected
		this.setAsSelected(refinementGroupId, refinementItemId);
		this.dispatch(this.setRefinementSelections(this.productSearchResults.refinementSelections));
		this.dispatch(this.setRefinementGroups(this.productSearchResults.refinementGroups));
	}

	//  Method used to work out which refinements to mark as available/disabled/selected
	//  based on the list of products that matched the customers last selection.
	// ===============================================================
	public filterOutRefinements(availableRefinementGroups: IRefinementGroup[]) {
		// groups we get from from api -> all available groups and refinements for search
		availableRefinementGroups.forEach((groupElement: IRefinementGroup) => {
			const refinementGroupId = groupElement.groupId;

			// refinements from store
			this.productSearchResults.refinementGroups[
				this.findIndexOfGroup(this.productSearchResults.refinementGroups, refinementGroupId)
			].refinements.forEach((refinementElement: IRefinement) => {
				const itemRefinementElementId = refinementElement.id;
				const refinementItemIdMatched = this.refinementItemIdIsInProductAvailableRefinements(
					groupElement.refinements,
					itemRefinementElementId,
				);

				if (!this.lastSelectionWasMadeIn(refinementGroupId)) {
					if (this.productSearchResults.lastActionWasToNarrowRefinement === true) {
						if (
							(this.isSelected(refinementGroupId, itemRefinementElementId) ||
								this.isSelectedButDisabled(refinementGroupId, itemRefinementElementId)) &&
							!refinementItemIdMatched
						) {
							this.setAsSelectedButDisabled(refinementGroupId, itemRefinementElementId);
						} else if (!refinementItemIdMatched) {
							this.setAsDisabled(refinementGroupId, itemRefinementElementId);
						} else if (this.isDisabled(refinementGroupId, itemRefinementElementId) && refinementItemIdMatched) {
							this.setAsAvailable(refinementGroupId, itemRefinementElementId);
						} else if (
							this.isSelectedButDisabled(refinementGroupId, itemRefinementElementId) &&
							refinementItemIdMatched
						) {
							this.setAsSelected(refinementGroupId, itemRefinementElementId);
						}
					} else {
						if (
							(this.isSelected(refinementGroupId, itemRefinementElementId) ||
								this.isSelectedButDisabled(refinementGroupId, itemRefinementElementId)) &&
							!refinementItemIdMatched
						) {
							this.setAsSelectedButDisabled(refinementGroupId, itemRefinementElementId);
						} else if (
							(this.isSelected(refinementGroupId, itemRefinementElementId) ||
								this.isSelectedButDisabled(refinementGroupId, itemRefinementElementId)) &&
							refinementItemIdMatched
						) {
							this.setAsSelected(refinementGroupId, itemRefinementElementId);
						} else if (this.isDisabled(refinementGroupId, itemRefinementElementId) && refinementItemIdMatched) {
							this.setAsAvailable(refinementGroupId, itemRefinementElementId);
						} else if (
							this.isDisabled(refinementGroupId, itemRefinementElementId) &&
							!refinementItemIdMatched &&
							!this.otherRefinementSelectionsExistApartFrom(refinementGroupId)
						) {
							this.setAsAvailable(refinementGroupId, itemRefinementElementId);
						} else if (this.isAvailable(refinementGroupId, itemRefinementElementId) && !refinementItemIdMatched) {
							this.setAsDisabled(refinementGroupId, itemRefinementElementId);
						}
					}
				} else if (this.productSearchResults.lastActionWasToNarrowRefinement === false) {
					if (this.isSelected(groupElement.groupId, itemRefinementElementId)) {
						this.setAsSelected(refinementGroupId, itemRefinementElementId);
					} else if (!this.otherRefinementSelectionsExistApartFrom(refinementGroupId)) {
						this.setAsAvailable(refinementGroupId, itemRefinementElementId);
					}
				} else if (this.isDisabled(groupElement.groupId, itemRefinementElementId) && refinementItemIdMatched) {
					this.setAsAvailable(refinementGroupId, itemRefinementElementId);
				}
			});

			const newRefinementGroups = this.productSearchResults.refinementGroups.map((x) => Object.assign({}, x));
			this.dispatch(this.setRefinementGroups(newRefinementGroups));
		});
	}

	// Method to determine if the given refinement item is in the list of matches for the
	// last
	// ===============================================================
	private refinementItemIdIsInProductAvailableRefinements(
		availableProductRefinements: IRefinement[],
		refinementItemId: number,
	) {
		return availableProductRefinements.filter((x) => x.id === refinementItemId)[0] ? true : false;
	}

	// Method used to determine if the given refinement group id is of the same group
	// that the last selection was made.
	// ===============================================================
	private lastSelectionWasMadeIn(refinementGroupId: number) {
		return this.productSearchResults.lastSelectedRefinementGroupId === refinementGroupId;
	}

	// Method used to determine if the user has selected other refinements in other
	// groups than the given refinement group id.
	// ===============================================================
	private otherRefinementSelectionsExistApartFrom(refinementGroupId: number) {
		let refinementSelectionsCount = 0;

		for (let i = this.productSearchResults.refinementSelections.length - 1; i >= 0; --i) {
			if (this.productSearchResults.refinementSelections[i].groupId !== refinementGroupId) {
				refinementSelectionsCount += this.productSearchResults.refinementSelections[i].refinements.length;
			}
		}

		return refinementSelectionsCount > 0;
	}

	// Helper methods to determine the state of a refinment.
	// ===============================================================
	private setAsSelectedButDisabled(refinementGroupId: number, refinementItemId: number) {
		const refinementItem = this.getRefinementFromArray(
			this.productSearchResults.refinementGroups,
			refinementGroupId,
			refinementItemId,
		);
		if (refinementItem) {
			refinementItem.selected = true;
			refinementItem.disabled = true;
			refinementItem.available = false;
		}
	}

	private setAsSelected(refinementGroupId: number, refinementItemId: number) {
		const refinementItem = this.getRefinementFromArray(
			this.productSearchResults.refinementGroups,
			refinementGroupId,
			refinementItemId,
		);
		if (refinementItem) {
			refinementItem.selected = true;
			refinementItem.disabled = false;
			refinementItem.available = false;
		}
	}

	private setAsAvailable(refinementGroupId: number, refinementItemId: number) {
		const refinementItem = this.getRefinementFromArray(
			this.productSearchResults.refinementGroups,
			refinementGroupId,
			refinementItemId,
		);
		if (refinementItem) {
			refinementItem.selected = false;
			refinementItem.disabled = false;
			refinementItem.available = true;
		}
	}

	private setAsDisabled(refinementGroupId: number, refinementItemId: number) {
		const refinementItem = this.getRefinementFromArray(
			this.productSearchResults.refinementGroups,
			refinementGroupId,
			refinementItemId,
		);
		if (refinementItem) {
			refinementItem.selected = false;
			refinementItem.disabled = true;
			refinementItem.available = false;
		}
	}

	private isDisabled(refinementGroupId: number, refinementItemId: number) {
		const refinementItem = this.getRefinementFromArray(
			this.productSearchResults.refinementGroups,
			refinementGroupId,
			refinementItemId,
		);
		if (refinementItem && refinementItem.disabled) {
			return true;
		}
		return false;
	}

	private isAvailable(refinementGroupId: number, refinementItemId: number) {
		const refinementItem = this.getRefinementFromArray(
			this.productSearchResults.refinementGroups,
			refinementGroupId,
			refinementItemId,
		);
		if (refinementItem && refinementItem.available) {
			return true;
		}
		return false;
	}

	private isSelected(refinementGroupId: number, refinementItemId: number) {
		const refinementItem = this.getRefinementFromArray(
			this.productSearchResults.refinementGroups,
			refinementGroupId,
			refinementItemId,
		);
		if (refinementItem && refinementItem.selected) {
			return true;
		}
		return false;
	}

	private isSelectedButDisabled(refinementGroupId: number, refinementItemId: number) {
		return this.isSelected(refinementGroupId, refinementItemId) && this.isDisabled(refinementGroupId, refinementItemId);
	}

	// Helper methods
	// ===============================================================
	private getRefinementFromArray(
		refinementGroups: IRefinementGroup[],
		refinementGroupId: number,
		refinementItemId: number,
	): IRefinement {
		return refinementGroups
			.filter((x) => x.groupId === refinementGroupId)[0]
			.refinements.filter((y) => y.id === refinementItemId)[0];
	}

	private findIndexOfRefinement(refinements: IRefinement[], refinementItemId: number) {
		for (let i = refinements.length - 1; i >= 0; --i) {
			if (refinements[i].id === refinementItemId) {
				return i;
			}
		}
		return -1;
	}

	private findIndexOfGroup(groups: IRefinementGroup[], groupId: number) {
		for (let i = groups.length - 1; i >= 0; --i) {
			if (groups[i].groupId === groupId) {
				return i;
			}
		}
		return -1;
	}
}
